import endLabel from '@/assets/images/status/status-label-end.png';
import holdLabel from '@/assets/images/status/status-label-hold.png';
import noticeLabel from '@/assets/images/status/status-label-notice.png';
import { LOTTERY_STATUS, OAUTH_PROVIDER } from '@/constants';
import { getOauthLink } from '@/features/oauth/api/getOauthLink';
import type { OauthLink } from '@/features/oauth/types';
import { Format } from '@/lib/dateFns';
import { newDate } from '@/utils/date';
import { add } from 'date-fns';
import { formatDateAboutJa, formatDateCommon } from '../../../utils/formatters';
import type {
  LotteryDrawHistoryLineup,
  LotteryDrawResultLineup,
  LotteryEventDetail,
  LotteryGoods,
  PrizeConvertedGoodsItem,
} from '../types';

// 景品ラベル内の合計在庫を返す（無限の場合はInfinity）
export const totalStock = (goodsList: Array<LotteryGoods>) => {
  let count = 0;
  goodsList.forEach((goods) => {
    count += goods.isStockLimit ? Number(goods.stock) : Number.POSITIVE_INFINITY;
  });
  return count;
};

// ユーザーが引いている場合は確率がずれてるので、履歴の方を参照する
export const odds = (
  lineupId: number,
  originOdds: number,
  lotteryDrawHistoryLineup?: Array<LotteryDrawHistoryLineup>,
) => {
  const targetHistoryPrize = lotteryDrawHistoryLineup?.find((historyLineup) => historyLineup.id === lineupId);
  return targetHistoryPrize ? targetHistoryPrize.odds : originOdds;
};

// グッズ入手済みかどうか
export const isGotGoods = (
  lineupId: number,
  goods: LotteryGoods,
  lotteryDrawHistoryLineup?: Array<LotteryDrawHistoryLineup>,
) => {
  const targetHistoryLineup = lotteryDrawHistoryLineup?.find((historyLineup) => historyLineup.id === lineupId);
  const targetGoods = targetHistoryLineup?.goods.find((historyGoods) => historyGoods.id === goods.id);
  return targetGoods ? targetGoods.acquiredCount > 0 : false;
};

/**
   * 演出キーを演出文字と演出値（ランク値）に変換する
   * # くじ演出の仕様
      ## 演出名
      なんでも可能。

      ## キー
      `賞-ランク` として登録する
      A賞で金の場合は`A-2`とする。

      1: 虹
      2: 金
      3: 銀

      賞はアルファベット1文字まで自由に。

      ※ dev環境を参考に
   */
export const createStagingInfo = (stagingKey: string) => {
  if (!stagingKey) {
    return {
      stagingText: '',
      stagingRank: '',
    };
  }
  const stgArr = stagingKey.split('-');
  return {
    stagingText: stgArr[0],
    stagingRank: stgArr[1],
  };
};

// 獲得景品配列をグッズ配列に変換する
// ついでに演出情報も付与
export const prizeList2goodsList = (prizeList: Array<LotteryDrawResultLineup>) => {
  return prizeList.reduce((arr: Array<PrizeConvertedGoodsItem>, prize: LotteryDrawResultLineup) => {
    const stagingInfo = createStagingInfo(prize.stagingKey);
    const convertedGoods = arr.concat(
      prize.goods.map((g) => {
        return {
          ...g,
          ...stagingInfo,
          benefitMultiDrawCount: prize.benefitMultiDrawCount,
        };
      }),
    );

    // sort順に並び替え
    convertedGoods.sort((a, b) => {
      return a.sort - b.sort;
    });
    return convertedGoods;
  }, []);
};

// イベントステータスラベル
export const eventStatusLabel = (eventStatus: string) => {
  switch (eventStatus) {
    case LOTTERY_STATUS.upcoming.value:
      return noticeLabel.src;
    case LOTTERY_STATUS.is_session.value:
      return holdLabel.src;
    default:
      return endLabel.src;
  }
};

// イベントページのツイッターシェア文言
export const twitterShareText = (eventName: string, eventEndAt: string) => {
  let text = eventName;
  if (eventEndAt) {
    text += ` ${formatDateCommon(eventEndAt, Format.dateTime)}まで！`;
  }
  return `${text} #RAFFLE #ラッフル`;
};

// くじ配送目安テキスト
// 例：1月上旬
export const estimatedShippingDateText = ({
  eventEndAt,
  estimatedShippingDate,
}: { eventEndAt?: string; estimatedShippingDate?: string }) => {
  let startEstimatedShippingDate: Date;
  if (!estimatedShippingDate) {
    // イベント終了日から起算して90日後、EndAtがなければ空文字
    if (!eventEndAt) return '';
    startEstimatedShippingDate = add(newDate(eventEndAt), { days: 90 });
  } else {
    startEstimatedShippingDate = newDate(estimatedShippingDate);
  }
  return `お届け目安：${formatDateAboutJa(startEstimatedShippingDate)}`;
};

// くじ配送目安テキストの表示有無
// EndAtとShippingDateが両方未設定の場合は非表示
export const showEstimatedShippingDate = (eventEndAt?: string, estimatedShippingDate?: string | null) => {
  return eventEndAt || estimatedShippingDate;
};

export const checkNeedsDAccountLink = async (event: LotteryEventDetail, isLogin: boolean) => {
  // dAccount連携くじじゃない場合は固定でfalse
  if (!event.relationAccountRequired?.dAccount) return false;
  // 未ログインは固定でtrue（ボタンはdアカ連携用のテキストにしたいため）
  if (!isLogin) return true;

  // それ以外の場合は連携状態を確認
  const { result: dCheck } = await getOauthLink();
  return dCheck.some((item: OauthLink) => item.providerKey === OAUTH_PROVIDER.daccount.value && !item.providerStatus);
};

// 送料があるか
export const hasShippingFee = (shippingFee?: number, additionalShippingFee?: number) => {
  if (shippingFee && shippingFee > 0) return true;
  if (additionalShippingFee && additionalShippingFee > 0) return true;
  return false;
};
